/**
 * Tree of Collaboration Block - Frontend interactivity
 *
 * Root/Trunk: shared behavior — hover → transient box; click → pin like leaves (stays on block leave,
 *   not replaced by hovering elsewhere). Root/trunk use the same amber hover glow in CSS.
 * Leaves: click selects branch if needed; with branch open, click leaf → branch card + leaf panel (opposite side).
 * Hover tooltips are suppressed while any card is open (pinned info box or branch/leaf side panels).
 */
document.addEventListener('DOMContentLoaded', () => {
	const blocks = document.querySelectorAll('.tree-of-collaboration');
	blocks.forEach((block) => initTreeBlock(block));
});

function initTreeBlock(block) {
	const svg = block.querySelector('.tree-of-collaboration__svg');
	const infoBox = block.querySelector('.tree-of-collaboration__info-box');
	const infoTitle = block.querySelector('.tree-of-collaboration__info-title');
	const infoContent = block.querySelector('.tree-of-collaboration__info-content');
	const infoLink = block.querySelector('.tree-of-collaboration__info-link');
	const linksContainer = block.querySelector('.tree-of-collaboration__links');
	const linksList = block.querySelector('.tree-of-collaboration__links-list');
	const branchCards = block.querySelectorAll('.tree-of-collaboration__branch-card');
	const leafPanel = block.querySelector('.tree-of-collaboration__leaf-panel');
	const leafTitle = block.querySelector('.tree-of-collaboration__leaf-card-title');
	const leafContent = block.querySelector('.tree-of-collaboration__leaf-card-content');
	const leafLink = block.querySelector('.tree-of-collaboration__leaf-card-link');

	let selectedBranch = null;
	let hoverTimeout = null;
	let infoPinned = false;
	/** @type {null | 'leaf' | 'root' | 'trunk'} */
	let pinnedSource = null;
	/** SVG path for leaf-pinned card; used to toggle off on second click */
	let pinnedLeafEl = null;
	/** Leaf path whose details are shown in the leaf panel (click, branch open) */
	let leafPanelLeafEl = null;

	function contentToHtml(content) {
		if (!content || typeof content !== 'string') return '';
		const t = content.trim();
		if (!t) return '';
		return escapeHtml(t).replace(/\r\n|\r|\n/g, '<br />');
	}

	function escapeHtml(text) {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}

	function hasBlockingCards() {
		return infoPinned || selectedBranch !== null;
	}

	function syncLeafLinkHighlight() {
		if (!linksList) return;
		linksList.querySelectorAll('a.is-leaf-active').forEach((a) => {
			a.classList.remove('is-leaf-active');
			a.removeAttribute('aria-current');
		});
		if (!leafPanelLeafEl) return;
		const idx = leafPanelLeafEl.getAttribute('data-leaf-index');
		if (idx === null || idx === '') return;
		const match = linksList.querySelector('a[data-leaf-index="' + idx + '"]');
		if (match) {
			match.classList.add('is-leaf-active');
			match.setAttribute('aria-current', 'true');
		}
	}

	function hideLeafPanel() {
		leafPanelLeafEl = null;
		if (leafPanel) {
			leafPanel.classList.add('tree-of-collaboration__leaf-panel--hidden');
			leafPanel.setAttribute('aria-hidden', 'true');
		}
		if (leafTitle) leafTitle.textContent = '';
		if (leafContent) leafContent.innerHTML = '';
		if (leafLink) {
			leafLink.href = '#';
			leafLink.style.display = 'none';
			leafLink.setAttribute('aria-hidden', 'true');
		}
		syncLeafLinkHighlight();
	}

	function showLeafPanelFromData(data) {
		if (!leafPanel || !leafTitle || !leafContent || !leafLink) return;
		try {
			const d = typeof data === 'string' ? JSON.parse(data) : data;
			leafTitle.textContent = d.title || '';
			leafContent.innerHTML = contentToHtml(d.content || '');
			const link = (d.link || '').trim();
			if (link) {
				leafLink.href = link;
				leafLink.style.display = 'inline-flex';
				leafLink.removeAttribute('aria-hidden');
			} else {
				leafLink.href = '#';
				leafLink.style.display = 'none';
				leafLink.setAttribute('aria-hidden', 'true');
			}
			leafPanel.classList.remove('tree-of-collaboration__leaf-panel--hidden');
			leafPanel.setAttribute('aria-hidden', 'false');
			syncLeafLinkHighlight();
		} catch (e) {
			hideLeafPanel();
		}
	}

	function unpinInfoBox() {
		infoPinned = false;
		pinnedSource = null;
		pinnedLeafEl = null;
		infoBox.classList.remove('is-pinned');
	}

	function showInfoBox(data, x, y, options = {}) {
		const pin = Boolean(options.pin);
		const source = options.source || null;
		const pinnedLeafElement = options.pinnedLeafElement || null;
		const horizontalHint = options.horizontalHint || null;
		if (!data) return;
		if (pin && source !== 'leaf') {
			hideLeafPanel();
		}
		try {
			const d = typeof data === 'string' ? JSON.parse(data) : data;
			if (source === 'leaf') {
				infoBox.classList.add('tree-of-collaboration__info-box--leaf');
			} else {
				infoBox.classList.remove('tree-of-collaboration__info-box--leaf');
			}
			infoTitle.textContent = d.title || '';
			infoContent.innerHTML = contentToHtml(d.content || '');
			const link = (d.link || '').trim();
			if (link) {
				infoLink.href = link;
				infoLink.style.display = 'inline-flex';
				infoLink.removeAttribute('aria-hidden');
			} else {
				infoLink.href = '#';
				infoLink.style.display = 'none';
				infoLink.setAttribute('aria-hidden', 'true');
			}
			infoBox.classList.add('is-visible');
			if (pin) {
				if (source === 'leaf') {
					clearRootTrunkHighlight();
				}
				infoPinned = true;
				pinnedSource = source;
				infoBox.classList.add('is-pinned');
				if (source === 'leaf') {
					pinnedLeafEl = pinnedLeafElement;
				} else {
					pinnedLeafEl = null;
				}
			} else {
				unpinInfoBox();
			}
			positionInfoBox(infoBox, x, y, { horizontalHint });
			if (pin) {
				updateExploreNavActive();
			}
		} catch (e) {
			// ignore parse errors
		}
	}

	function hideInfoBox() {
		if (infoPinned) return;
		infoBox.classList.remove('is-visible', 'is-pinned', 'tree-of-collaboration__info-box--leaf');
	}

	function clearRootTrunkHighlight() {
		svg.querySelectorAll('.tree-root, .tree-trunk').forEach((el) => el.classList.remove('is-selected'));
	}

	function setRootTrunkHighlight(source) {
		clearRootTrunkHighlight();
		if (source === 'root') {
			svg.querySelectorAll('.tree-root').forEach((el) => el.classList.add('is-selected'));
		} else if (source === 'trunk') {
			svg.querySelectorAll('.tree-trunk').forEach((el) => el.classList.add('is-selected'));
		}
	}

	function forceHideInfoBox() {
		unpinInfoBox();
		clearRootTrunkHighlight();
		infoBox.classList.remove('is-visible', 'tree-of-collaboration__info-box--leaf');
		updateExploreNavActive();
	}

	/**
	 * @param {HTMLElement} box
	 * @param {number} x - clientX
	 * @param {number} y - clientY
	 * @param {{ horizontalHint?: 'left' | 'right' | 'default' }} [opts]
	 */
	function positionInfoBox(box, x, y, opts = {}) {
		const wrapper = block.querySelector('.tree-of-collaboration__wrapper');
		const rect = wrapper ? wrapper.getBoundingClientRect() : block.getBoundingClientRect();
		const boxRect = box.getBoundingClientRect();
		const relX = x - rect.left;
		const relFrac = rect.width > 0 ? relX / rect.width : 0.5;
		const margin = 16;
		let left;
		const hint = opts.horizontalHint || 'default';
		if (hint === 'left' || (hint === 'default' && relFrac > 0.62)) {
			left = relX - boxRect.width - margin;
		} else if (hint === 'right' || (hint === 'default' && relFrac < 0.38)) {
			left = relX + margin;
		} else {
			left = relX + margin;
		}
		let top = y - rect.top - 8;
		if (left + boxRect.width > rect.width - margin) left = rect.width - boxRect.width - margin;
		if (left < margin) left = margin;
		if (top < margin) top = margin;
		if (top + boxRect.height > rect.height - margin) top = rect.height - boxRect.height - margin;
		box.style.left = left + 'px';
		box.style.top = top + 'px';
	}

	function setSelectedBranch(branch) {
		if (branch !== selectedBranch) {
			hideLeafPanel();
		}
		selectedBranch = branch;
		block.classList.remove('is-branch-black-selected', 'is-branch-blue-selected', 'is-branch-teal-selected');
		if (branch) {
			block.classList.add('is-branch-' + branch + '-selected');
			svg.querySelectorAll('.tree-branch').forEach((el) => {
				el.classList.remove('is-selected');
				if (el.classList.contains('tree-branch-' + branch) && !el.classList.contains('tree-branch-' + branch + '-leaf')) {
					el.classList.add('is-selected');
				}
			});
			showLinksForBranch(branch);
			showBranchCard(branch);
		} else {
			linksContainer.classList.add('tree-of-collaboration__links--hidden');
			linksContainer.setAttribute('aria-hidden', 'true');
			branchCards.forEach((card) => (card.style.display = 'none'));
			hideLeafPanel();
		}
		updateExploreNavActive();
	}

	function getExploreAnchorClientPoint() {
		const explore = block.querySelector('.tree-of-collaboration__explore');
		const wrapper = block.querySelector('.tree-of-collaboration__wrapper');
		const wRect = wrapper ? wrapper.getBoundingClientRect() : block.getBoundingClientRect();
		if (explore) {
			const r = explore.getBoundingClientRect();
			return { x: r.left + r.width / 2, y: r.bottom + 20 };
		}
		return { x: wRect.left + wRect.width / 2, y: wRect.top + 48 };
	}

	function updateExploreNavActive() {
		const details = block.querySelector('.tree-of-collaboration__explore-branches');
		block.querySelectorAll('.tree-of-collaboration__explore-action.is-active').forEach((el) => el.classList.remove('is-active'));
		block.querySelectorAll('.tree-of-collaboration__explore-option.is-active').forEach((el) => el.classList.remove('is-active'));
		if (details) {
			details.classList.toggle('has-branch-selection', Boolean(selectedBranch));
		}
		if (infoPinned && pinnedSource === 'root') {
			const b = block.querySelector('.tree-of-collaboration__explore-action[data-explore-target="root"]');
			if (b) b.classList.add('is-active');
		}
		if (infoPinned && pinnedSource === 'trunk') {
			const b = block.querySelector('.tree-of-collaboration__explore-action[data-explore-target="trunk"]');
			if (b) b.classList.add('is-active');
		}
		if (selectedBranch) {
			const opt = block.querySelector('.tree-of-collaboration__explore-option[data-branch="' + selectedBranch + '"]');
			if (opt) opt.classList.add('is-active');
		}
	}

	function showLinksForBranch(branch) {
		linksList.innerHTML = '';
		const paths = svg.querySelectorAll('.tree-branch-' + branch + '-leaf');
		/** TreeBase can emit multiple paths per logical leaf (same data-leaf-index); list each leaf once. */
		const seenLeafIndexes = new Set();
		const links = [];
		paths.forEach((path) => {
			const leafIndex = path.getAttribute('data-leaf-index');
			if (leafIndex !== null && leafIndex !== '') {
				if (seenLeafIndexes.has(leafIndex)) {
					return;
				}
				seenLeafIndexes.add(leafIndex);
			}
			const content = path.getAttribute('data-content');
			if (!content) return;
			try {
				const d = JSON.parse(content);
				const link = (d.link || '').trim();
				const title = (d.title || '').trim();
				if (link) {
					links.push({
						url: link,
						title: title || link,
						leafIndex: leafIndex !== null && leafIndex !== '' ? leafIndex : '',
					});
				}
			} catch (e) { }
		});
		links.forEach((item) => {
			const li = document.createElement('li');
			const a = document.createElement('a');
			a.href = item.url;
			a.target = '_blank';
			a.rel = 'noopener noreferrer';
			a.textContent = item.title;
			if (item.leafIndex !== '') {
				a.setAttribute('data-leaf-index', item.leafIndex);
			}
			li.appendChild(a);
			linksList.appendChild(li);
		});
		linksContainer.classList.remove('tree-of-collaboration__links--hidden');
		linksContainer.setAttribute('aria-hidden', 'false');
		syncLeafLinkHighlight();
	}

	function showBranchCard(branch) {
		branchCards.forEach((card) => {
			if (card.getAttribute('data-branch') === branch) {
				card.style.display = 'block';
			} else {
				card.style.display = 'none';
			}
		});
	}

	function getBranchHoverContent(target, branchName) {
		const isLeaf = target.classList.contains('tree-branch-' + branchName + '-leaf');
		if (isLeaf) {
			return target.getAttribute('data-content');
		}
		return target.getAttribute('data-branch-content');
	}

	function horizontalHintFromEvent(e) {
		const wrapper = block.querySelector('.tree-of-collaboration__wrapper');
		const rect = wrapper ? wrapper.getBoundingClientRect() : block.getBoundingClientRect();
		const relFrac = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
		if (relFrac > 0.62) return 'left';
		if (relFrac < 0.38) return 'right';
		return 'default';
	}

	/** Hover shows transient tooltip; blocked while a branch is open or any info box is pinned */
	function onHotspotEnter(content, e, hoverSource = null) {
		clearTimeout(hoverTimeout);
		if (hasBlockingCards()) return;
		showInfoBox(content, e.clientX, e.clientY, {
			pin: false,
			source: hoverSource,
			horizontalHint: horizontalHintFromEvent(e),
		});
	}

	function onHotspotLeave() {
		hoverTimeout = setTimeout(hideInfoBox, 100);
	}

	function onHotspotMove(e) {
		if (hasBlockingCards()) return;
		if (infoBox.classList.contains('is-visible') && !infoPinned) {
			positionInfoBox(infoBox, e.clientX, e.clientY, { horizontalHint: horizontalHintFromEvent(e) });
		}
	}

	function bindRootTrunkPart(selector, source) {
		block.querySelectorAll(selector).forEach((el) => {
			el.addEventListener('mouseenter', (e) => {
				onHotspotEnter(el.getAttribute('data-content'), e, source);
			});
			el.addEventListener('mouseleave', onHotspotLeave);
			el.addEventListener('mousemove', onHotspotMove);
			el.addEventListener('click', (e) => {
				e.stopPropagation();
				clearTimeout(hoverTimeout);
				if (infoPinned && pinnedSource === source) {
					forceHideInfoBox();
					return;
				}
				setRootTrunkHighlight(source);
				showInfoBox(el.getAttribute('data-content'), e.clientX, e.clientY, {
					pin: true,
					source,
					horizontalHint: horizontalHintFromEvent(e),
				});
			});
		});
	}

	bindRootTrunkPart('.tree-root', 'root');
	bindRootTrunkPart('.tree-trunk', 'trunk');

	['black', 'blue', 'teal'].forEach((branchName) => {
		const branchPaths = svg.querySelectorAll('.tree-branch-' + branchName);
		const branchPath = svg.querySelector('.tree-branch-' + branchName + ':not(.tree-branch-' + branchName + '-leaf)');

		if (branchPath) {
			branchPath.addEventListener('click', (e) => {
				e.stopPropagation();
				clearTimeout(hoverTimeout);
				forceHideInfoBox();
				setSelectedBranch(selectedBranch === branchName ? null : branchName);
			});
		}

		branchPaths.forEach((el) => {
			el.addEventListener('mouseenter', (e) => {
				const t = e.currentTarget;
				const isLeaf = t.classList.contains('tree-branch-' + branchName + '-leaf');
				const hoverSource = isLeaf ? 'leaf' : 'branch';
				onHotspotEnter(getBranchHoverContent(t, branchName), e, hoverSource);
			});
			el.addEventListener('mouseleave', onHotspotLeave);
			el.addEventListener('mousemove', onHotspotMove);
			el.addEventListener('click', (e) => {
				const isLeaf = e.currentTarget.classList.contains('tree-branch-' + branchName + '-leaf');
				if (!isLeaf) return;
				e.stopPropagation();
				clearTimeout(hoverTimeout);
				const leafEl = e.currentTarget;
				const leafData = leafEl.getAttribute('data-content');
				if (selectedBranch !== branchName) {
					forceHideInfoBox();
					setSelectedBranch(branchName);
					leafPanelLeafEl = leafEl;
					showLeafPanelFromData(leafData);
					return;
				}
				if (leafPanelLeafEl === leafEl) {
					hideLeafPanel();
					unpinInfoBox();
					infoBox.classList.remove('is-visible', 'tree-of-collaboration__info-box--leaf');
					return;
				}
				forceHideInfoBox();
				leafPanelLeafEl = leafEl;
				showLeafPanelFromData(leafData);
			});
		});
	});

	block.addEventListener('click', (e) => {
		if (e.target.closest('.tree-of-collaboration__branch-card-close')) {
			e.preventDefault();
			e.stopPropagation();
			clearTimeout(hoverTimeout);
			forceHideInfoBox();
			setSelectedBranch(null);
			return;
		}
		if (e.target.closest('.tree-of-collaboration__leaf-card-close')) {
			e.preventDefault();
			e.stopPropagation();
			clearTimeout(hoverTimeout);
			hideLeafPanel();
			unpinInfoBox();
			infoBox.classList.remove('is-visible', 'tree-of-collaboration__info-box--leaf');
			return;
		}
		const t = e.target.closest(
			'.tree-of-collaboration__explore-action[data-explore-target], .tree-of-collaboration__explore-option[data-explore-target]'
		);
		if (!t || !block.contains(t)) return;
		const target = t.getAttribute('data-explore-target');
		if (!target) return;
		e.preventDefault();
		if (target === 'branch') {
			const branchName = t.getAttribute('data-branch');
			if (!branchName) return;
			clearTimeout(hoverTimeout);
			forceHideInfoBox();
			setSelectedBranch(selectedBranch === branchName ? null : branchName);
			return;
		}
		if (target === 'root' || target === 'trunk') {
			const pathSel = target === 'root' ? '.tree-root' : '.tree-trunk';
			const pathEl = svg.querySelector(pathSel);
			if (!pathEl) return;
			clearTimeout(hoverTimeout);
			if (infoPinned && pinnedSource === target) {
				forceHideInfoBox();
				return;
			}
			setRootTrunkHighlight(target);
			const pt = getExploreAnchorClientPoint();
			showInfoBox(pathEl.getAttribute('data-content'), pt.x, pt.y, {
				pin: true,
				source: target,
				horizontalHint: horizontalHintFromEvent({ clientX: pt.x, clientY: pt.y }),
			});
		}
	});

	block.addEventListener('mouseleave', () => {
		clearTimeout(hoverTimeout);
		if (infoPinned) return;
		forceHideInfoBox();
	});
}
